/**
 * SOLA Chat Store - Ephemeral Session Only
 *
 * Clean, normalized state management with NO persistence.
 * Every refresh starts fresh - no history, no saved messages.
 *
 * Architecture:
 * - Normalized state: messagesById (O(1) lookup) + messageIds (order)
 * - Selective subscriptions: Components only re-render when their data changes
 * - In-memory only: All data lost on refresh
 * - Cross-tab sync: BroadcastChannel for multi-tab state synchronization
 *
 * Design Principles:
 * 1. Single source of truth
 * 2. Immutable updates (via Immer)
 * 3. Minimal re-renders (selective subscriptions)
 * 4. Ephemeral by design - fresh start every time
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { logger } from './logger'
import type { SolaMessage } from './types'

/**
 * Store state interface
 *
 * IMPORTANT: We store full SolaMessage format with parts array intact.
 * This preserves all complexity including:
 * - Text parts
 * - Tool calls and results
 * - File attachments
 * - Streaming state
 */
interface ChatState {
  // ===  Normalized Messages ===
  messagesById: Record<string, SolaMessage>
  messageIds: string[]

  // === UI State (not persisted) ===
  status: 'idle' | 'streaming' | 'error'
  streamingMessageId: string | null
  usage: any | null // Token usage from AI responses

  // === UI Settings ===
  settingsOpen: boolean
  debugMode: boolean

  // === Actions ===
  addMessage: (message: SolaMessage, source?: 'local' | 'remote') => void
  updateMessage: (id: string, updates: Partial<SolaMessage>) => void
  setMessages: (messages: SolaMessage[]) => void
  deleteMessage: (id: string) => void
  clearMessages: (source?: 'local' | 'remote') => void

  setStatus: (status: ChatState['status']) => void
  setStreamingMessageId: (id: string | null) => void
  setUsage: (usage: any) => void
  setSettingsOpen: (open: boolean) => void
  setDebugMode: (enabled: boolean) => void

  reset: () => void
}

// Initial state
const initialState = {
  messagesById: {},
  messageIds: [],
  status: 'idle' as const,
  streamingMessageId: null,
  usage: null,
  settingsOpen: false,
  debugMode: false, // Hydrated from localStorage in useEffect to avoid SSR mismatch
}

// Cross-tab synchronization
const SYNC_CHANNEL = 'sola-chat-sync'
let broadcastChannel: BroadcastChannel | null = null

if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  broadcastChannel = new BroadcastChannel(SYNC_CHANNEL)
  logger.sync('BroadcastChannel initialized', { channel: SYNC_CHANNEL })
}

/**
 * Create the chat store - in-memory only, no persistence
 */
export const useChatStore = create<ChatState>()(
  subscribeWithSelector(
    (set, get) => ({
      ...initialState,

        // === Message Actions ===

        addMessage: (message, source = 'local') => {
          logger.state('Adding message', {
            messageId: message.id,
            role: message.role,
            partsCount: message.parts?.length || 0,
            source,
          })

          const state = get()

          // Prevent duplicates
          if (state.messagesById[message.id]) {
            logger.warn('Message already exists', { messageId: message.id })
            return
          }

          // Store message reference from AI SDK
          set({
            messagesById: {
              ...state.messagesById,
              [message.id]: message, // Live reference, not clone!
            },
            messageIds: [...state.messageIds, message.id],
          })

          // Broadcast to other tabs (only if this is a local message, not from another tab)
          // This prevents infinite broadcast loops across tabs
          if (source === 'local' && broadcastChannel) {
            broadcastChannel.postMessage({ type: 'MESSAGE_ADDED', message })
            logger.sync('Message broadcast to other tabs', { messageId: message.id })
          }
        },

        updateMessage: (id, updates) => {
          logger.state('Updating message', { messageId: id, updates })

          const state = get()
          if (!state.messagesById[id]) {
            logger.warn('Message not found for update', { messageId: id })
            return
          }

          set({
            messagesById: {
              ...state.messagesById,
              [id]: { ...state.messagesById[id], ...updates },
            },
          })
        },

        setMessages: (messages) => {
          logger.state('Setting messages', {
            count: messages.length,
            messages: JSON.stringify(messages.map(m => ({
              id: m.id,
              role: m.role,
              partsCount: m.parts?.length || 0,
              createdAt: m.createdAt
            }))),
          })

          // Store message references from AI SDK
          // NOTE: AI SDK v6 stable uses structuredClone(), so these are snapshots
          // not live references. useChatSync re-syncs after streaming completes.
          const messagesById: Record<string, SolaMessage> = {}
          const messageIds: string[] = []

          messages.forEach((message) => {
            messagesById[message.id] = message
            messageIds.push(message.id)
          })

          set({ messagesById, messageIds })

          logger.state('Messages set complete', {
            messageCount: get().messageIds.length,
            messageIds: JSON.stringify(get().messageIds),
            messagesById: JSON.stringify(Object.keys(get().messagesById)),
          })
        },

        deleteMessage: (id) => {
          logger.state('Deleting message', { messageId: id })

          const state = get()
          const { [id]: _, ...remainingMessages } = state.messagesById

          set({
            messagesById: remainingMessages,
            messageIds: state.messageIds.filter((msgId) => msgId !== id),
          })
        },

        clearMessages: (source = 'local') => {
          logger.state('Clearing all messages', {
            count: get().messageIds.length,
            source,
          })

          set({
            messagesById: {},
            messageIds: [],
          })

          // Broadcast clear to other tabs (only if this is a local clear, not from another tab)
          // This prevents infinite broadcast loops across tabs
          if (source === 'local' && broadcastChannel) {
            broadcastChannel.postMessage({ type: 'MESSAGES_CLEARED' })
            logger.sync('Clear broadcast to other tabs')
          }
        },

        // === UI Actions ===

        setStatus: (status) => {
          set({ status })
        },

        setStreamingMessageId: (id) => {
          set({ streamingMessageId: id })
        },

        setUsage: (usage) => {
          set({ usage })
        },

        setSettingsOpen: (open) => {
          set({ settingsOpen: open })
        },

        setDebugMode: (enabled) => {
          localStorage.setItem('sola-debug-mode', String(enabled))
          set({ debugMode: enabled })
        },

        // === Reset ===

        reset: () => {
          logger.state('Resetting store to initial state')

          set(initialState)
        },
      })
  )
)

// Listen for cross-tab messages
if (broadcastChannel) {
  broadcastChannel.onmessage = (event) => {
    const { type, message } = event.data

    logger.sync('Received cross-tab message', { type })

    if (type === 'MESSAGE_ADDED' && message) {
      // Pass 'remote' source to prevent re-broadcasting (which would cause infinite loop)
      useChatStore.getState().addMessage(message, 'remote')
    } else if (type === 'MESSAGES_CLEARED') {
      // Pass 'remote' source to prevent re-broadcasting (which would cause infinite loop)
      useChatStore.getState().clearMessages('remote')
    }
  }
}

// Selectors for selective subscriptions
export const selectMessageIds = (state: ChatState) => state.messageIds
export const selectMessage = (id: string) => (state: ChatState) => state.messagesById[id]
export const selectStatus = (state: ChatState) => state.status
export const selectUsage = (state: ChatState) => state.usage
export const selectSettingsOpen = (state: ChatState) => state.settingsOpen
export const selectDebugMode = (state: ChatState) => state.debugMode

// Export types
export type { ChatState, SolaMessage as Message }
